import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-user-details-form',
  templateUrl: './user-details-form.component.html',
  styleUrl: './user-details-form.component.scss',
})
export class UserDetailsFormComponent {
  form!: FormGroup;
  isEditing = false;
  saving = false;

  emailForm!: FormGroup;
  isEditingEmail = false;
  emailSaving = false;
  pendingEmail = '';

  constructor(
    private fb: FormBuilder,
    protected userService: UserService,
    private nbToastrService: NbToastrService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      email: [''],
      role: [''],
    });
    this.emailForm = this.fb.group({
      new_email: ['', [Validators.required, Validators.email]],
      confirm_email: ['', [Validators.required, Validators.email]],
      current_password: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.userService.me$.subscribe((org) => {
      if (org) {
        this.form.patchValue(org);
      }
    });
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const me = this.userService.me$.value;
    if (!me || this.saving) {
      return;
    }

    // Only the name is editable here — email changes go through the
    // double-opt-in email-change flow below, never through this PATCH.
    this.saving = true;
    this.userService
      .updateUser({
        ...me,
        name: this.form.get('name')?.value,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.isEditing = false;
          this.nbToastrService.success(
            this.translate.instant('pages.settings.detailsSaved'),
            this.translate.instant('toasts.success')
          );
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  cancelEdit() {
    this.isEditing = false;
    const me = this.userService.me$.value;
    if (me) {
      this.form.patchValue(me);
    }
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }

  // --- Email change (admin only, double-opt-in via confirmation link) ---

  get isAdmin(): boolean {
    const role = this.userService.me$.value?.role;
    return role === 'admin' || role === 'super_admin';
  }

  startEmailEdit() {
    this.emailForm.reset({
      new_email: '',
      confirm_email: '',
      current_password: '',
    });
    this.isEditingEmail = true;
  }

  cancelEmailEdit() {
    this.isEditingEmail = false;
  }

  onEmailSubmit() {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    if (
      this.emailForm.get('new_email')?.value.trim().toLowerCase() !==
      this.emailForm.get('confirm_email')?.value.trim().toLowerCase()
    ) {
      this.emailForm.get('confirm_email')!.setErrors({ notMatch: true });
      this.nbToastrService.danger(
        this.translate.instant('pages.settings.emailChange.errorMismatch'),
        this.translate.instant('pages.settings.emailChange.errorTitle')
      );
      return;
    }

    this.emailSaving = true;
    this.userService
      .requestEmailChange(
        this.emailForm.get('new_email')?.value,
        this.emailForm.get('current_password')?.value
      )
      .subscribe({
        next: (res) => {
          this.emailSaving = false;
          this.isEditingEmail = false;
          this.pendingEmail = res.data.pending_email;
          this.nbToastrService.success(
            this.translate.instant('pages.settings.emailChange.toastBody'),
            this.translate.instant('pages.settings.emailChange.toastTitle')
          );
        },
        error: (err: HttpErrorResponse) => {
          this.emailSaving = false;
          this.nbToastrService.danger(
            this.translate.instant(this.emailErrorKey(err)),
            this.translate.instant('pages.settings.emailChange.errorTitle')
          );
        },
      });
  }

  showEmailInputError(name: string) {
    return (
      this.emailForm.get(name)?.touched && this.emailForm.get(name)?.invalid
    );
  }

  private emailErrorKey(err: HttpErrorResponse): string {
    const detail: string = err?.error?.detail || '';
    if (err.status === 409) {
      return 'pages.settings.emailChange.errorTaken';
    }
    if (err.status === 429) {
      return 'pages.settings.emailChange.errorRateLimit';
    }
    if (detail.includes('password')) {
      return 'pages.settings.emailChange.errorPassword';
    }
    if (detail.includes('identical')) {
      return 'pages.settings.emailChange.errorSame';
    }
    return 'pages.settings.emailChange.errorGeneric';
  }
}
